import React, { useState, useEffect } from 'react';

const DynamicForm = ({ config, initialValues, onSave, onCancel }) => {
  const [formData, setFormData] = useState(initialValues || {});

  useEffect(() => {
    setFormData(initialValues || {});
  }, [initialValues]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      // Filter out non-serializable values
      const serializableFormData = JSON.parse(JSON.stringify(formData, (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (value instanceof HTMLElement || value === window) {
            return undefined;
          }
        }
        return value;
      }));

      // Pass form data to parent - let parent handle the API call
      // This allows parent to add additional data like geometry
      await onSave(serializableFormData);
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  return (
    <div>
      <div className="-space-y-px">
        {config.fields.map((field, index) => {
          const isFirst = index === 0;
          const isLast = index === config.fields.length - 1;
          const roundClass = isFirst && isLast
            ? 'rounded-md'
            : isFirst
              ? 'rounded-t-md'
              : isLast
                ? 'rounded-b-md'
                : 'rounded-none';

          if (field.type === 'select') {
            return (
              <div
                key={field.name}
                className={`${roundClass} bg-white px-3 pb-1.5 pt-2.5 outline outline-1 -outline-offset-1 outline-gray-300 focus-within:relative focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600`}
              >
                <label htmlFor={field.name} className="block text-xs font-medium text-gray-900">
                  {field.label}
                </label>
                <select
                  id={field.name}
                  name={field.name}
                  value={formData[field.name] || ''}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  className="block w-full text-gray-900 placeholder:text-gray-400 border-none outline-none focus:outline-none focus:shadow-none focus:ring-0 sm:text-sm/6"
                >
                  <option value="">Select</option>
                  {field.options.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
            );
          }

          if (field.type === 'checkbox') {
            return (
              <div
                key={field.name}
                className={`${roundClass} bg-white px-3 py-3 outline outline-1 -outline-offset-1 outline-gray-300 focus-within:relative focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 ${field.disabled ? 'bg-gray-50' : ''}`}
              >
                <div className="flex items-center">
                  <input
                    id={field.name}
                    name={field.name}
                    type="checkbox"
                    checked={formData[field.name] || false}
                    onChange={(e) => !field.disabled && handleInputChange(field.name, e.target.checked)}
                    disabled={field.disabled}
                    className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 ${field.disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                  />
                  <label htmlFor={field.name} className={`ml-2 block text-sm font-medium ${field.disabled ? 'text-gray-500' : 'text-gray-900'}`}>
                    {field.label}
                  </label>
                </div>
                {field.hint && (
                  <p className="mt-1 text-xs text-amber-600">{field.hint}</p>
                )}
              </div>
            );
          }

          return (
            <div
              key={field.name}
              className={`${roundClass} bg-white px-3 pb-1.5 pt-2.5 outline outline-1 -outline-offset-1 outline-gray-300 focus-within:relative focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600`}
            >
              <label htmlFor={field.name} className="block text-xs font-medium text-gray-900">
                {field.label}
              </label>
              <input
                id={field.name}
                name={field.name}
                type={field.type}
                value={formData[field.name] || ''}
                placeholder={formData[field.placeholder] || 'Enter a value'}
                onChange={(e) => handleInputChange(field.name, e.target.value)}
                required={field.required}
                className="block w-full text-gray-900 placeholder:text-gray-400 border-none outline-none  focus:outline-none focus:shadow-none focus:ring-0 sm:text-sm/6"
              />
            </div>
          );
        })}
      </div>
      <div className="mt-3">
        <button type="button" className="mr-3 rounded-md bg-indigo-50 px-3.5 py-2.5 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-100" onClick={handleSave}>
          Save and Close
        </button>
        <button type="button" className="mr-3 rounded-md bg-red-50 px-3.5 py-2.5 text-sm font-semibold text-red-600 shadow-sm hover:bg-red-100" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default DynamicForm;
